import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  ListItemAvatar,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Devices as DevicesIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Storage as StorageIcon,
  Cable as CableIcon,
  Router as RouterIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as TodoIcon
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Basic stats
  const [stats, setStats] = useState({
    devices: 0,
    deviceTypes: 0,
    locations: 0,
    characteristics: 0,
    loading: true
  });

  // Analytics data
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState('');

  const [recentDevices, setRecentDevices] = useState([]);
  const [devicesByType, setDevicesByType] = useState([]);
  const [monProfiles, setMonProfiles] = useState(0);

  useEffect(() => {
    // Always reload data when component mounts
    loadDashboardData();
    loadAnalytics();
    axios.get('/monitoring/profiles').then((r) => setMonProfiles((r.data || []).length)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboardData = async () => {
    try {
      const [devicesRes, typesRes, locsRes, charsRes] = await Promise.all([
        axios.get('/devices/'),
        axios.get('/device-types/'),
        axios.get('/locations/'),
        axios.get('/characteristics/')
      ]);

      const devices = devicesRes.data;
      const types = typesRes.data;
      const locations = locsRes.data;
      const characteristics = charsRes.data;

      setStats({
        devices: devices.total || devices.items?.length || 0,
        deviceTypes: types.length || 0,
        locations: locations.length || 0,
        characteristics: characteristics.length || 0,
        loading: false
      });

      // Get recent devices
      if (devices.items && devices.items.length > 0) {
        setRecentDevices(devices.items.slice(0, 5));
      }

      // Count devices by type
      if (devices.items && types.length > 0) {
        const typeMap = {};
        types.forEach(t => {
          typeMap[t.id] = { name: t.name, count: 0 };
        });
        devices.items.forEach(d => {
          if (typeMap[d.device_type_id]) {
            typeMap[d.device_type_id].count++;
          }
        });
        setDevicesByType(Object.values(typeMap).filter(t => t.count > 0));
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await axios.get('/reports/analytics/dashboard');
      setAnalytics(response.data);
    } catch (err) {
      setAnalyticsError('Не удалось загрузить аналитику');
      console.error(err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, onClick }) => (
    <Card
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s',
        '&:hover': onClick ? {
          boxShadow: 6,
          transform: 'translateY(-4px)'
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (stats.loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Загрузка...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Главная панель
      </Typography>

      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="h6">
          Добро пожаловать, {user?.email}!
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Система управления инвентаризацией оборудования
        </Typography>
      </Paper>

      {/* Onboarding checklist — hides itself once every step is done */}
      {(() => {
        const steps = [
          { label: 'Добавьте локации', desc: 'Здания, цеха, участки — основа карты и группировки', done: stats.locations > 0, to: '/locations' },
          { label: 'Добавьте устройства', desc: 'Вручную, импортом из таблицы или «Из скана» (файл агента/GLPI)', done: stats.devices > 0, to: '/devices' },
          { label: 'Свяжите устройства', desc: 'Нарисуйте связи или примите авто-предложения на карточке устройства', done: (analytics?.summary?.total_connections || 0) > 0, to: '/connections' },
          { label: 'Настройте мониторинг', desc: 'Профили опроса (ping/SNMP) или агент на ПК — состояние и доступность', done: monProfiles > 0, to: '/monitoring' },
        ];
        const doneCount = steps.filter((s) => s.done).length;
        if (doneCount === steps.length) return null;
        return (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="h6">С чего начать</Typography>
              <Chip size="small" label={`${doneCount} / ${steps.length}`} color={doneCount ? 'primary' : 'default'} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Опишите инфраструктуру по шагам — карта, матрицы и зависимости строятся автоматически.
            </Typography>
            <List dense>
              {steps.map((s, i) => (
                <ListItem key={i} disableGutters
                  secondaryAction={!s.done && <Button size="small" variant="outlined" onClick={() => navigate(s.to)}>Перейти</Button>}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    {s.done ? <CheckIcon color="success" /> : <TodoIcon color="disabled" />}
                  </ListItemAvatar>
                  <ListItemText primary={`${i + 1}. ${s.label}`} secondary={s.desc} />
                </ListItem>
              ))}
            </List>
          </Paper>
        );
      })()}

      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Общая статистика
      </Typography>

      {/* Main Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Всего устройств"
            value={stats.devices}
            icon={<DevicesIcon />}
            color="primary.main"
            onClick={() => navigate('/devices')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Типов устройств"
            value={stats.deviceTypes}
            icon={<CategoryIcon />}
            color="success.main"
            onClick={() => navigate('/device-types')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Локаций"
            value={stats.locations}
            icon={<LocationIcon />}
            color="warning.main"
            onClick={() => navigate('/locations')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Характеристик"
            value={stats.characteristics}
            icon={<StorageIcon />}
            color="info.main"
            onClick={() => navigate('/characteristics')}
          />
        </Grid>
      </Grid>

      {/* Analytics Section */}
      {analyticsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : analyticsError ? (
        <Alert severity="warning" sx={{ mb: 3 }}>{analyticsError}</Alert>
      ) : analytics && (
        <>
          {/* Additional Stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CableIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      Подключения
                    </Typography>
                  </Box>
                  <Typography variant="h3">{analytics.summary.total_connections}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <RouterIcon sx={{ mr: 1, color: '#9c27b0' }} />
                    <Typography variant="h6" color="text.secondary">
                      Подсети
                    </Typography>
                  </Box>
                  <Typography variant="h3">{analytics.summary.total_subnets}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: analytics.summary.incomplete_devices > 0 ? '#fff3e0' : 'inherit' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      Незаполненные
                    </Typography>
                  </Box>
                  <Typography variant="h3">{analytics.summary.incomplete_devices}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: analytics.summary.ip_conflicts > 0 ? '#ffebee' : 'inherit' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ErrorIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      IP конфликты
                    </Typography>
                  </Box>
                  <Typography variant="h3">{analytics.summary.ip_conflicts}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts: Devices by Type and Location */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Устройства по типам
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {analytics.devices_by_type && analytics.devices_by_type.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={analytics.devices_by_type}
                            dataKey="count"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {analytics.devices_by_type.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {analytics.devices_by_type.map((item, index) => (
                          <Chip
                            key={index}
                            label={`${item.name}: ${item.count}`}
                            size="small"
                            sx={{
                              bgcolor: COLORS[index % COLORS.length],
                              color: 'white'
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                      Нет данных
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Устройства по локациям (Top 10)
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {analytics.devices_by_location && analytics.devices_by_location.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={analytics.devices_by_location.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                      Нет данных
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Subnet Utilization */}
          {analytics.subnet_utilization && analytics.subnet_utilization.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Использование IP адресов в подсетях
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.subnet_utilization}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="assigned_ips" name="Назначено" stackId="a" fill="#00C49F" />
                    <Bar dataKey="reserved_ips" name="Зарезервировано" stackId="a" fill="#FFBB28" />
                    <Bar dataKey="free_ips" name="Свободно" stackId="a" fill="#e0e0e0" />
                  </BarChart>
                </ResponsiveContainer>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {analytics.subnet_utilization.map((subnet, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            {subnet.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Диапазон: {subnet.range_size} адресов
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Использовано: {subnet.total_ips} ({subnet.utilization_percent}%)
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={`Назначено: ${subnet.assigned_ips}`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                            <Chip
                              label={`Резерв: ${subnet.reserved_ips}`}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                            <Chip
                              label={`Свободно: ${subnet.free_ips}`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Recent Devices */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Последние добавленные устройства
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {recentDevices.length > 0 ? (
                <List>
                  {recentDevices.map((device) => (
                    <ListItem
                      key={device.id}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => navigate(`/devices/${device.id}`)}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <DevicesIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={device.name}
                        secondary={`${device.device_type_name} • ${device.location_name || 'Без локации'}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  Нет устройств. Добавьте первое устройство!
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Распределение по типам
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {devicesByType.length > 0 ? (
                <Box>
                  {devicesByType.slice(0, 5).map((type, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{type.name}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {type.count} ({stats.devices > 0 ? Math.round((type.count / stats.devices) * 100) : 0}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stats.devices > 0 ? (type.count / stats.devices) * 100 : 0}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  Нет данных для отображения
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Access */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Быстрый доступ
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-4px)'
              }
            }}
            onClick={() => navigate('/map')}
          >
            <Typography variant="h6" gutterBottom>
              Интерактивная карта
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Визуализация оборудования и связей
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-4px)'
              }
            }}
            onClick={() => navigate('/devices')}
          >
            <Typography variant="h6" gutterBottom>
              Управление устройствами
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Добавление, редактирование и удаление устройств
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-4px)'
              }
            }}
            onClick={() => navigate('/incomplete-devices')}
          >
            <Typography variant="h6" gutterBottom>
              Незаполненные устройства
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Просмотр устройств с незаполненными полями
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
